import React, { useState, useEffect } from 'react';
import isArray from 'lodash/isArray';
import ModalModule from '../modal';
import Utils from '../utils';
import UI from '../ui';

const { WrappedInputTextWithLabel, WrappedInputTextareaWithLabel, WrappedCheckbox } = UI;
const { withLayoutAndModalContexts, Validation } = Utils;
const { ModalButtonActionType } = ModalModule;

const notificationTypes = [
	{ Type: 'ShipmentCreated', Label: 'Shipment Created' },
	{ Type: 'DeliveryDateAnnounced', Label: 'Tracking Available' },
	{ Type: 'OutForDelivery', Label: 'Out for Delivery' },
	{ Type: 'Delivered', Label: 'Delivered' },
];

const EmailsTabPanel = props => {
	const {
		setNotificationSettingsConfig,
		triggers,
		replyToEmail,
		fromName,
		promotional,
		bccEmails,
		bccEmailsEnabled,
		setBccEmailsEnabled,
		isValidationNeeded,
	} = props;

	const [bccEmailsAsString, setBccEmailsAsString] = useState('');

	useEffect(() => {
		setBccEmailsEnabled(isArray(bccEmails) && bccEmails.length > 0);
		setBccEmailsAsString(bccEmails.map(email => email.EmailAddress).join(','));
	}, [bccEmails]);

	const isTriggerChecked = value =>
		triggers && triggers.some(trigger => trigger.NotificationType === value);

	const onFieldChange = event => {
		let {
			target: { value, name },
		} = event;

		if (name === 'Promotional') {
			value = [
				{
					ContentBlockType: 'Alert',
					Value: value,
				},
			];
			name = 'EmailConfig.ContentBlocks';
		}

		setNotificationSettingsConfig({ [name]: value });
	};

	const onBccEmailsEnabledFieldChange = event => {
		const {
			target: { checked },
		} = event;

		setBccEmailsEnabled(checked);

		// if not enabled and there are emails specified, then clean the bcc email list
		if (!checked && isArray(bccEmails) && bccEmails.length > 0) {
			setNotificationSettingsConfig({ 'EmailConfig.BCCEmails': [] });
		}
	};

	const onBccEmailsFieldChange = event => {
		let {
			target: { value, name },
		} = event;

		value = value
			.trim()
			.split(',')
			.filter(x => x)
			.map(EmailAddress => ({
				EmailAddress,
			}));
		name = 'EmailConfig.BCCEmails';

		setNotificationSettingsConfig({ [name]: value });
	};

	const onTriggerFieldChange = event => {
		const {
			target: { value, checked },
		} = event;
		const {
			modalContext: { showMessage },
		} = props;

		// build new triggers
		const newTriggers =
			// if notification found and not checked off
			triggers.find(trigger => trigger.NotificationType === value) && !checked
				? // remove it from the list
				  triggers.filter(trigger => trigger.NotificationType !== value)
				: // otherwise add checked off notification into the list
				  [
						...triggers,
						{
							NotificationType: value,
							Channels: [
								{
									ChannelType: 'Email',
								},
							],
						},
				  ];

		if (newTriggers.length === 0) {
			showMessage({
				data: {
					title: 'At Least One Shipment Status Must Be Active',
					description:
						'At least one Shipment Status must remain active.  If you wish not to send shipment notification emails, simply leave the Recipient Email Address field blank when printing.',
					primaryButtonType: ModalButtonActionType.Ok,
					primaryButtonText: 'Got It',
				},
			});
		} else {
			setNotificationSettingsConfig({
				Triggers: newTriggers,
			});
		}
	};

	return (
		<div className="branded-tracking-emails-tab-panel">
			<section className="email-status-container">
				<div className="shipping-status-heading-container">
					<h2 className="status">Shipment Status</h2>
					<h2>Send Email</h2>
				</div>
				{notificationTypes.map(notificationType => (
					<React.Fragment key={notificationType.Type}>
						<WrappedCheckbox
							inputId="cbTrigger"
							value={notificationType.Type}
							onChange={onTriggerFieldChange}
							checked={isTriggerChecked(notificationType.Type)}
							htmlFor="cbTrigger"
							dataqa="Trigger"
							name="Trigger"
						>
							<>{notificationType.Label}</>
						</WrappedCheckbox>
					</React.Fragment>
				))}
			</section>
			<section>
				<WrappedInputTextWithLabel
					label="Reply-To Email:"
					isInputInvalid={isValidationNeeded && !Validation.isValidEmail(replyToEmail)}
					errorMessage="Please enter a valid email address"
					showErrorBelow
					uiElementConfig={{
						value: replyToEmail || '',
						onChange: onFieldChange,
						maxLength: 100,
						dataqa: 'EmailConfig.ReplyToEmail',
						name: 'EmailConfig.ReplyToEmail',
					}}
				/>
			</section>
			<section>
				<WrappedInputTextWithLabel
					label="From Name:"
					isInputInvalid={isValidationNeeded && (fromName || '') === ''}
					errorMessage="From Name is required"
					showErrorBelow
					uiElementConfig={{
						value: fromName || '',
						onChange: onFieldChange,
						required: true,
						maxLength: 100,
						dataqa: 'EmailConfig.FromName',
						name: 'EmailConfig.FromName',
					}}
				/>
			</section>
			<section>
				<WrappedInputTextareaWithLabel
					label="Promotional Message:"
					uiElementConfig={{
						value: promotional || '',
						onChange: onFieldChange,
						rows: 5,
						cols: 31,
						dataqa: 'Promotional',
						name: 'Promotional',
					}}
				/>
			</section>
			<section>
				<WrappedCheckbox
					inputId="cbBccEmailsEnabled"
					value={bccEmailsEnabled}
					onChange={onBccEmailsEnabledFieldChange}
					checked={bccEmailsEnabled}
					htmlFor="cbBccEmailsEnabled"
					dataqa="BccEmailsEnabled"
					name="BccEmailsEnabled"
				>
					Send a copy of all emails to:
				</WrappedCheckbox>
				<WrappedInputTextWithLabel
					label=""
					isInputInvalid={
						isValidationNeeded &&
						bccEmailsEnabled &&
						(!isArray(bccEmails) ||
							bccEmails.length === 0 ||
							bccEmails.some(email => !Validation.isValidEmail(email.EmailAddress)))
					}
					errorMessage={
						!isArray(bccEmails) || !bccEmails.length
							? 'An email address is required in order to send a copy of all emails'
							: 'Please enter a valid email address'
					}
					showErrorBelow
					uiElementConfig={{
						value: bccEmailsAsString,
						disabled: !bccEmailsEnabled,
						onChange: onBccEmailsFieldChange,
						dataqa: 'BccEmailsAsString',
						name: 'BccEmailsAsString',
					}}
				/>
			</section>
		</div>
	);
};

export default withLayoutAndModalContexts(EmailsTabPanel);
